// repo/primary-care-connect/frontend/src/pages/Portal/CommunityNavigator/Uploads/UploadsPage.tsx

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import "./UploadsPage.css";
import { useNavigatorDialog } from "../useNavigatorDialog";
import { portalFetch } from "../../../../utils/portalRoleChoice";

type UploadBatchStatus =
  | "Uploaded"
  | "Processing"
  | "Processed"
  | "PartiallyProcessed"
  | "Failed"
  | "Cancelled";

type UploadBatch = {
  id: number;
  uploadedAt: string;
  batchName: string;
  sourceLabel: string;
  originalFileName: string;
  totalRows: number;
  createdCaseCount: number;
  skippedRowCount: number;
  errorRowCount: number;
  status: UploadBatchStatus;
};

type UploadBatchApiItem = {
  navigatorUploadBatchId: number;
  uploadedAt: string;
  batchName: string;
  sourceLabel: string | null;
  originalFileName: string | null;
  totalRows: number;
  createdCaseCount: number;
  skippedRowCount: number;
  errorRowCount: number;
  status: UploadBatchStatus;
};

type UploadBatchesApiResponse = {
  ok: boolean;
  mode?: string;
  items?: UploadBatchApiItem[];
  paging?: {
    total?: number;
    limit?: number;
    offset?: number;
  };
  error?: string;
};

type UploadResponse = {
  ok: boolean;
  mode?: string;
  batch?: {
    navigatorUploadBatchId: number;
    batchName: string;
    sourceLabel: string;
    originalFileName: string;
    status: UploadBatchStatus;
    totalRows: number;
    createdCaseCount: number;
    skippedRowCount: number;
    errorRowCount: number;
    processingNotes?: string | null;
  };
  error?: string;
};

export const UPLOAD_HISTORY_PAGE_SIZE = 50;

const requiredColumns = ["Patient Name", "DOB", "Phone", "FSA", "Email"];

const csvTemplateContent =
  "Patient Name,DOB,Phone,FSA,Email\n" +
  "Jane Smith,1975-04-22,905-555-1234,L6M,jane@example.com\n";

const workflowSteps = [
  {
    title: "Upload list",
    description:
      "Upload an OHT or community patient list with minimum contact information.",
  },
  {
    title: "Validate rows",
    description:
      "The system checks for patient name, date of birth, phone, FSA, and email.",
  },
  {
    title: "Create draft cases",
    description:
      "Valid rows become PatientLead records and linked NavigatorCase records.",
  },
  {
    title: "Complete intake later",
    description:
      "Navigators confirm full postal code and intake details from the Cases page.",
  },
  {
    title: "Forward only when ready",
    description:
      "Doctor-facing requests are created only after intake, matching, and navigator review.",
  },
];

function formatNumber(value: number): string {
  return value.toLocaleString();
}

function formatDateTime(value: string): string {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function getStatusLabel(status: UploadBatchStatus): string {
  switch (status) {
    case "PartiallyProcessed":
      return "Partially processed";
    default:
      return status;
  }
}

export function getUploadHistoryOffset(page: number): number {
  const safePage = Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
  return (safePage - 1) * UPLOAD_HISTORY_PAGE_SIZE;
}

export function getUploadHistoryTotalPages(total: number): number {
  const safeTotal = Number.isFinite(total) && total > 0 ? Math.floor(total) : 0;
  return Math.max(1, Math.ceil(safeTotal / UPLOAD_HISTORY_PAGE_SIZE));
}

export function getClampedUploadHistoryPage(page: number, total: number): number {
  const totalPages = getUploadHistoryTotalPages(total);
  const safePage = Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
  return Math.min(safePage, totalPages);
}

export function buildUploadHistoryUrl(page: number): string {
  const params = new URLSearchParams();
  params.set("limit", String(UPLOAD_HISTORY_PAGE_SIZE));
  params.set("offset", String(getUploadHistoryOffset(page)));
  return `/api/portal/community-navigator/uploads?${params.toString()}`;
}

function mapApiBatch(item: UploadBatchApiItem): UploadBatch {
  return {
    id: item.navigatorUploadBatchId,
    uploadedAt: item.uploadedAt,
    batchName: item.batchName || "Untitled batch",
    sourceLabel: item.sourceLabel || "Not specified",
    originalFileName: item.originalFileName || "—",
    totalRows: Number(item.totalRows || 0),
    createdCaseCount: Number(item.createdCaseCount || 0),
    skippedRowCount: Number(item.skippedRowCount || 0),
    errorRowCount: Number(item.errorRowCount || 0),
    status: item.status,
  };
}

function parseCsvText(text: string): string[][] {
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentValue = "";
  let insideQuotes = false;

  const cleanedText = text.replace(/^\uFEFF/, "");

  for (let index = 0; index < cleanedText.length; index += 1) {
    const char = cleanedText[index];
    const nextChar = cleanedText[index + 1];

    if (char === '"' && insideQuotes && nextChar === '"') {
      currentValue += '"';
      index += 1;
      continue;
    }

    if (char === '"') {
      insideQuotes = !insideQuotes;
      continue;
    }

    if (char === "," && !insideQuotes) {
      currentRow.push(currentValue.trim());
      currentValue = "";
      continue;
    }

    if ((char === "\n" || char === "\r") && !insideQuotes) {
      if (char === "\r" && nextChar === "\n") {
        index += 1;
      }

      currentRow.push(currentValue.trim());
      currentValue = "";

      if (currentRow.some((value) => value.length > 0)) {
        rows.push(currentRow);
      }

      currentRow = [];
      continue;
    }

    currentValue += char;
  }

  currentRow.push(currentValue.trim());

  if (currentRow.some((value) => value.length > 0)) {
    rows.push(currentRow);
  }

  return rows;
}

function parseCsvRows(text: string): Record<string, string>[] {
  const parsedRows = parseCsvText(text);

  if (parsedRows.length < 2) {
    throw new Error("CSV must include a header row and at least one patient row.");
  }

  const headers = parsedRows[0].map((header) => header.trim());

  if (!headers.length || headers.every((header) => !header)) {
    throw new Error("CSV header row is missing.");
  }

  const normalizedRows = parsedRows.slice(1).map((row) => {
    const item: Record<string, string> = {};

    headers.forEach((header, index) => {
      if (!header) return;
      item[header] = row[index] || "";
    });

    return item;
  });

  return normalizedRows.filter((row) =>
    Object.values(row).some((value) => String(value || "").trim().length > 0)
  );
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(String(reader.result || ""));
    };

    reader.onerror = () => {
      reject(new Error("Unable to read selected file."));
    };

    reader.readAsText(file);
  });
}

function downloadCsvTemplate() {
  const blob = new Blob([csvTemplateContent], {
    type: "text/csv;charset=utf-8",
  });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = "community-navigator-upload-template.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  window.URL.revokeObjectURL(url);
}

export default function UploadsPage() {
  const [isUploadPanelOpen, setIsUploadPanelOpen] = useState(false);
  const uploadDialogRef = useRef<HTMLElement>(null);
  const uploadBatchNameRef = useRef<HTMLInputElement>(null);
  const uploadFileRef = useRef<HTMLInputElement>(null);
  const uploadErrorRef = useRef<HTMLDivElement>(null);
  const uploadResultRef = useRef<HTMLDivElement>(null);
  const suppressUploadReturnFocusRef = useRef(false);
  const [uploadBatches, setUploadBatches] = useState<UploadBatch[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [historyReload, setHistoryReload] = useState(0);
  const [isLoadingBatches, setIsLoadingBatches] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const totalPages = getUploadHistoryTotalPages(historyTotal);

  const [batchName, setBatchName] = useState("");
  const [sourceLabel, setSourceLabel] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [hasConfirmedUploadRule, setHasConfirmedUploadRule] = useState(false);
  const [isSubmittingUpload, setIsSubmittingUpload] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<UploadResponse["batch"] | null>(
    null
  );

  const canSubmitUpload = useMemo(() => {
    return (
      batchName.trim().length > 0 &&
      sourceLabel.trim().length > 0 &&
      Boolean(selectedFile) &&
      hasConfirmedUploadRule &&
      !isSubmittingUpload
    );
  }, [
    batchName,
    hasConfirmedUploadRule,
    isSubmittingUpload,
    selectedFile,
    sourceLabel,
  ]);

  const resetUploadForm = useCallback(() => {
    setBatchName("");
    setSourceLabel("");
    setSelectedFile(null);
    setHasConfirmedUploadRule(false);
    setIsSubmittingUpload(false);
    setUploadError(null);
    setUploadResult(null);
  }, []);

  const closeUploadPanel = useCallback(() => {
    if (isSubmittingUpload) return;
    setIsUploadPanelOpen(false);
    resetUploadForm();
  }, [isSubmittingUpload, resetUploadForm]);

  useEffect(() => {
    const controller = new AbortController();
    let clamped = false;

    async function loadHistory() {
      setIsLoadingBatches(true);
      setLoadError(null);

      try {
        const response = await portalFetch(buildUploadHistoryUrl(page), {
          method: "GET",
          credentials: "include",
          signal: controller.signal,
        });

        const data = (await response.json()) as UploadBatchesApiResponse;

        if (controller.signal.aborted) {
          return;
        }

        if (!response.ok || !data.ok) {
          throw new Error(data?.error || "Unable to load upload batches.");
        }

        const parsedTotal = Number(data.paging?.total);
        const total =
          Number.isFinite(parsedTotal) && parsedTotal >= 0 ? parsedTotal : 0;
        const nextPage = getClampedUploadHistoryPage(page, total);

        if (nextPage !== page) {
          clamped = true;
          setHistoryTotal(total);
          setPage(nextPage);
          return;
        }

        if (controller.signal.aborted) {
          return;
        }

        const items = Array.isArray(data.items) ? data.items : [];
        setUploadBatches(items.map(mapApiBatch));
        setHistoryTotal(total);
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
          return;
        }

        setUploadBatches([]);
        setLoadError(
          err instanceof Error ? err.message : "Unable to load upload batches."
        );
      } finally {
        if (!controller.signal.aborted && !clamped) {
          setIsLoadingBatches(false);
        }
      }
    }

    loadHistory();

    return () => {
      controller.abort();
    };
  }, [page, historyReload]);

  async function handleUploadSubmit() {
    if (!selectedFile) {
      setUploadError("Please select a CSV file.");
      return;
    }

    if (!selectedFile.name.toLowerCase().endsWith(".csv")) {
      setUploadError("Only CSV uploads are supported in this first version.");
      return;
    }

    setIsSubmittingUpload(true);
    setUploadError(null);
    setUploadResult(null);

    try {
      const text = await readFileAsText(selectedFile);
      const rows = parseCsvRows(text);

      if (!rows.length) {
        throw new Error("CSV file does not contain any patient rows.");
      }

      const response = await portalFetch("/api/portal/community-navigator/uploads", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          batchName: batchName.trim(),
          sourceLabel: sourceLabel.trim(),
          originalFileName: selectedFile.name,
          rows,
        }),
      });

      const data = (await response.json()) as UploadResponse;

      if (!response.ok || !data.ok) {
        throw new Error(data?.error || "Unable to upload patient list.");
      }

      setUploadResult(data.batch || null);
      if (page === 1) {
        setHistoryReload((current) => current + 1);
      } else {
        setPage(1);
      }
    } catch (err) {
      setUploadError(
        err instanceof Error ? err.message : "Unable to upload patient list."
      );
    } finally {
      setIsSubmittingUpload(false);
    }
  }

  useNavigatorDialog({
    open: isUploadPanelOpen,
    onDismiss: closeUploadPanel,
    dismissDisabled: isSubmittingUpload,
    dialogRef: uploadDialogRef,
    initialFocusRef: uploadBatchNameRef,
    suppressReturnFocusRef: suppressUploadReturnFocusRef,
  });

  useLayoutEffect(() => {
    if (!isUploadPanelOpen || !uploadError) return;

    if (
      uploadError === "Please select a CSV file." ||
      uploadError === "Only CSV uploads are supported in this first version."
    ) {
      uploadFileRef.current?.focus();
      return;
    }

    uploadErrorRef.current?.focus();
  }, [isUploadPanelOpen, uploadError]);

  useLayoutEffect(() => {
    if (!isUploadPanelOpen || !uploadResult) return;
    uploadResultRef.current?.focus();
  }, [isUploadPanelOpen, uploadResult]);

  return (
    <div className="cn-uploads-page">
      <section className="cn-uploads-hero">
        <div>
          <p className="cn-uploads-eyebrow">Community Navigator Portal</p>
          <h1>Uploads</h1>
          <p className="cn-uploads-subtitle">
            Upload OHT or community patient lists and create draft navigator
            cases for follow-up.
          </p>
          <p className="cn-uploads-note">
            Uploads create draft cases only. Full postal code and intake details
            are confirmed later from the Cases page before matching or
            forwarding.
          </p>
        </div>

        <button
          type="button"
          className="cn-uploads-primary-action"
          onClick={() => {
            resetUploadForm();
            setIsUploadPanelOpen(true);
          }}
        >
          Upload patient list
        </button>
      </section>

      <section className="cn-uploads-grid">
        <article className="cn-uploads-card cn-uploads-card-large">
          <div className="cn-uploads-card-header">
            <div>
              <h2>How uploads work</h2>
              <p>
                Uploads bring OHT or community lists into the navigator queue.
                They do not complete intake or send requests to doctors.
              </p>
            </div>
          </div>

          <div className="cn-uploads-workflow">
            {workflowSteps.map((step, index) => (
              <div className="cn-uploads-workflow-step" key={step.title}>
                <span>{index + 1}</span>
                <div>
                  <strong>{step.title}</strong>
                  <p>{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </article>

        <article className="cn-uploads-card">
          <div className="cn-uploads-card-header">
            <div>
              <h2>Required columns</h2>
              <p>
                These are the minimum fields needed to create a draft navigator
                case from an uploaded list.
              </p>
            </div>
          </div>

          <div className="cn-uploads-column-list">
            {requiredColumns.map((column) => (
              <span key={column}>{column}</span>
            ))}
          </div>

          <div className="cn-uploads-warning">
            <strong>Location rule</strong>
            <p>
              FSA is accepted at upload. Full postal code must be confirmed
              later before intake can be completed.
            </p>
          </div>

          <div className="cn-uploads-modal-actions">
            <button
              type="button"
              className="cn-uploads-secondary-button"
              onClick={downloadCsvTemplate}
            >
              Download CSV template
            </button>
          </div>
        </article>
      </section>

      <section className="cn-uploads-table-card">
        <div className="cn-uploads-section-header">
          <div>
            <h2>Upload batches</h2>
            <p>
              Review uploaded list batches, row counts, created cases, and rows
              that need attention.
            </p>
          </div>

          <button
            type="button"
            className="cn-uploads-secondary-button"
            onClick={() => {
              setHistoryReload((current) => current + 1);
            }}
            disabled={isLoadingBatches}
          >
            {isLoadingBatches ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {loadError ? (
          <div className="cn-uploads-warning">
            <strong>Upload batches could not load</strong>
            <p>{loadError}</p>
          </div>
        ) : null}

        {isLoadingBatches ? (
          <div className="cn-uploads-empty">
            <div className="cn-uploads-empty-icon">↻</div>
            <h3>Loading upload batches</h3>
            <p>
              Checking for OHT or community list uploads in your navigator
              scope.
            </p>
          </div>
        ) : uploadBatches.length > 0 ? (
          <div
            className="cn-uploads-table-wrap"
            tabIndex={0}
            role="region"
            aria-label="Upload batches table"
          >
            <table className="cn-uploads-table">
              <thead>
                <tr>
                  <th>Uploaded</th>
                  <th>Batch name</th>
                  <th>Source</th>
                  <th>File name</th>
                  <th>Total rows</th>
                  <th>Cases created</th>
                  <th>Skipped</th>
                  <th>Errors</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {uploadBatches.map((batch) => (
                  <tr key={batch.id}>
                    <td>{formatDateTime(batch.uploadedAt)}</td>
                    <td>{batch.batchName}</td>
                    <td>{batch.sourceLabel}</td>
                    <td>{batch.originalFileName}</td>
                    <td>{formatNumber(batch.totalRows)}</td>
                    <td>{formatNumber(batch.createdCaseCount)}</td>
                    <td>{formatNumber(batch.skippedRowCount)}</td>
                    <td>{formatNumber(batch.errorRowCount)}</td>
                    <td>
                      <span
                        className={`cn-uploads-status cn-uploads-status-${batch.status.toLowerCase()}`}
                      >
                        {getStatusLabel(batch.status)}
                      </span>
                    </td>
                    <td>
                      <Link
                        to={`/portal/community-navigator/cases?uploadBatchId=${batch.id}&scope=all`}
                        className="cn-uploads-table-link"
                      >
                        View cases
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="cn-uploads-empty">
            <div className="cn-uploads-empty-icon">↑</div>
            <h3>No uploads yet</h3>
            <p>
              Upload an OHT or community patient list to create draft navigator
              cases. Valid rows will appear in the Cases page for intake
              completion.
            </p>
            <button
              type="button"
              className="cn-uploads-empty-action"
              onClick={() => {
                resetUploadForm();
                setIsUploadPanelOpen(true);
              }}
            >
              Upload patient list
            </button>
          </div>
        )}

        {totalPages > 1 ? (
          <div className="cn-uploads-pagination" aria-label="Upload history pagination">
            <button
              type="button"
              className="cn-uploads-pagination-button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={isLoadingBatches || page <= 1}
            >
              Previous
            </button>
            <span className="cn-uploads-pagination-status">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="cn-uploads-pagination-button"
              onClick={() =>
                setPage((current) => Math.min(totalPages, current + 1))
              }
              disabled={isLoadingBatches || page >= totalPages}
            >
              Next
            </button>
          </div>
        ) : null}
      </section>

      <section className="cn-uploads-rules">
        <article>
          <h2>What this page does</h2>
          <ul>
            <li>Creates draft PatientLead records from valid upload rows.</li>
            <li>Creates linked NavigatorCase records for navigator follow-up.</li>
            <li>Tracks upload batches, skipped rows, and errors.</li>
          </ul>
        </article>

        <article>
          <h2>What this page does not do</h2>
          <ul>
            <li>Does not complete navigator intake.</li>
            <li>Does not run matching automatically.</li>
            <li>Does not forward anything to doctors or clinics.</li>
          </ul>
        </article>
      </section>

      {isUploadPanelOpen ? (
        <div
          className="cn-uploads-modal-backdrop"
          role="presentation"
          onClick={() => {
            if (isSubmittingUpload) return;
            closeUploadPanel();
          }}
        >
          <section
            className="cn-uploads-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cn-uploads-modal-title"
            tabIndex={-1}
            ref={uploadDialogRef}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="cn-uploads-modal-header">
              <div>
                <h2 id="cn-uploads-modal-title">Upload patient list</h2>
                <p>
                  Upload a CSV file with Patient Name, DOB, Phone, FSA, and
                  Email. Valid rows will create draft navigator cases.
                </p>
              </div>

              <button
                type="button"
                className="cn-uploads-modal-close"
                onClick={closeUploadPanel}
                disabled={isSubmittingUpload}
                aria-label="Close upload panel"
              >
                ×
              </button>
            </div>

            {uploadResult ? (
              <div
                className="cn-uploads-warning"
                role="status"
                tabIndex={-1}
                ref={uploadResultRef}
              >
                <strong>Upload processed</strong>
                <p>
                  {formatNumber(uploadResult.createdCaseCount)} case(s) created,{" "}
                  {formatNumber(uploadResult.skippedRowCount)} skipped, and{" "}
                  {formatNumber(uploadResult.errorRowCount)} error(s).
                </p>
                {uploadResult.processingNotes ? (
                  <p>{uploadResult.processingNotes}</p>
                ) : null}
              </div>
            ) : null}

            {uploadError ? (
              <div
                className="cn-uploads-warning"
                role="alert"
                tabIndex={-1}
                ref={uploadErrorRef}
              >
                <strong>Upload could not be processed</strong>
                <p>{uploadError}</p>
              </div>
            ) : null}

            <div className="cn-uploads-form-preview">
              <button
                type="button"
                className="cn-uploads-secondary-button"
                onClick={downloadCsvTemplate}
                disabled={isSubmittingUpload}
              >
                Download CSV template
              </button>

              <label>
                Batch name
                <input
                  ref={uploadBatchNameRef}
                  type="text"
                  placeholder="e.g. CCHOHT unattached list — May 2026"
                  value={batchName}
                  onChange={(event) => setBatchName(event.target.value)}
                  disabled={isSubmittingUpload}
                />
              </label>

              <label>
                Source label
                <input
                  type="text"
                  placeholder="e.g. Connected Care Halton OHT"
                  value={sourceLabel}
                  onChange={(event) => setSourceLabel(event.target.value)}
                  disabled={isSubmittingUpload}
                />
              </label>

              <label>
                File
                <input
                  ref={uploadFileRef}
                  type="file"
                  accept=".csv,text/csv"
                  disabled={isSubmittingUpload}
                  onChange={(event) => {
                    setUploadError(null);
                    setUploadResult(null);
                    setSelectedFile(event.target.files?.[0] || null);
                  }}
                />
              </label>

              <div className="cn-uploads-confirmation-preview">
                <input
                  type="checkbox"
                  checked={hasConfirmedUploadRule}
                  disabled={isSubmittingUpload}
                  onChange={(event) =>
                    setHasConfirmedUploadRule(event.target.checked)
                  }
                />
                <span>
                  I understand this upload will create draft navigator cases.
                  Full postal code and intake details must be confirmed before
                  matching or forwarding.
                </span>
              </div>
            </div>

            <div className="cn-uploads-modal-actions">
              <button
                type="button"
                className="cn-uploads-secondary-button"
                onClick={closeUploadPanel}
                disabled={isSubmittingUpload}
              >
                {uploadResult ? "Close" : "Cancel"}
              </button>

              {uploadResult?.navigatorUploadBatchId ? (
                <Link
                  to={`/portal/community-navigator/cases?uploadBatchId=${uploadResult.navigatorUploadBatchId}&scope=all`}
                  className="cn-uploads-primary-action"
                  onClick={() => {
                    suppressUploadReturnFocusRef.current = true;
                    closeUploadPanel();
                  }}
                >
                  View created cases
                </Link>
              ) : (
                <button
                  type="button"
                  className={
                    canSubmitUpload
                      ? "cn-uploads-primary-action"
                      : "cn-uploads-disabled-button"
                  }
                  disabled={!canSubmitUpload}
                  onClick={handleUploadSubmit}
                >
                  {isSubmittingUpload ? "Uploading..." : "Upload list"}
                </button>
              )}
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}